/**
 * `/admin/departments` — admin-managed departments (SPEC §6, §7.1, §9.1, §10, M8).
 *
 * Departments stopped being a fixed enum. An administrator can create, rename,
 * recolour, reorder, and deactivate them, and move a report between them. The
 * page reuses the admin conventions the rest of the portal already uses:
 *
 * * an edit **replaces** the department's mutable fields wholesale (name,
 *   accent, sort order, active) rather than patching one at a time, the same
 *   predictability the grant editor is built around;
 * * every mutation invalidates `['admin']`, `['me']`, and `['departments']`, so
 *   the registry view, the effective-access panels, and the public navigation
 *   all refresh — moving a report changes who can see it (§7.1);
 * * the delete is guarded: the button is disabled while the department holds any
 *   report or any grant, and the server refuses it anyway (§7.1). Deactivate
 *   instead, which hides it from navigation and new grants while its rows live.
 *
 * Nothing here is protection: `/api/admin/*` is behind `require_admin`
 * server-side, and stable URLs / 404-never-403 are unchanged.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Badge, Button, Card, ConfirmDialog, Grid, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import type { AdminReportRow, DepartmentRow } from '../../lib/adminTypes'
import { ApiError } from '../../lib/api'

const DEFAULT_ACCENT = '#0F6E6E'

/** A wayfinding swatch (§9.2): a small rounded block of the department accent. */
function Swatch({ accent }: { accent: string }) {
  return (
    <span
      aria-hidden
      className="inline-block h-4 w-4 shrink-0 rounded-[var(--radius-sm)] border border-[var(--color-rule)]"
      style={{ background: accent }}
    />
  )
}

/** A colour picker paired with its hex, so the value is always a valid `#rrggbb`. */
function AccentField({
  value,
  onChange,
}: {
  value: string
  onChange: (accent: string) => void
}) {
  return (
    <label className="block">
      <span className="block text-[length:var(--text-xs)] font-medium">Accent</span>
      <span className="mt-1 flex items-center gap-2">
        <input
          type="color"
          aria-label="Accent colour"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className="h-8 w-10 cursor-pointer rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] p-0.5"
        />
        <span className="numeric text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          {value.toUpperCase()}
        </span>
      </span>
    </label>
  )
}

// --- create -------------------------------------------------------------------

function CreateDepartment({
  nextSortOrder,
  onCreated,
}: {
  nextSortOrder: number
  onCreated: () => void
}) {
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [accent, setAccent] = useState(DEFAULT_ACCENT)

  const create = useMutation({
    mutationFn: () =>
      adminApi.createDepartment({ key, name, accent, sort_order: nextSortOrder }),
    onSuccess: () => {
      setKey('')
      setName('')
      setAccent(DEFAULT_ACCENT)
      onCreated()
    },
  })
  const fields = create.error instanceof ApiError ? create.error.fields : {}

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate()
      }}
    >
      <div className="w-48">
        <TextField
          label="Key"
          value={key}
          required
          hint="lowercase, immutable"
          onChange={(event) => {
            setKey(event.target.value)
          }}
          error={fields.key}
        />
      </div>
      <div className="w-56">
        <TextField
          label="Name"
          value={name}
          required
          onChange={(event) => {
            setName(event.target.value)
          }}
          error={fields.name}
        />
      </div>
      <AccentField value={accent} onChange={setAccent} />
      <Button variant="primary" type="submit" disabled={create.isPending}>
        {create.isPending ? 'Creating…' : 'Create department'}
      </Button>
      {create.error !== null && fields.key === undefined && fields.name === undefined && (
        <div className="w-full">
          <ErrorState error={create.error} />
        </div>
      )}
    </form>
  )
}

// --- edit ---------------------------------------------------------------------

function EditDepartment({
  department,
  saving,
  onSave,
}: {
  department: DepartmentRow
  saving: boolean
  onSave: (patch: {
    name: string
    accent: string
    sort_order: number
    is_active: boolean
  }) => void
}) {
  const [name, setName] = useState(department.name)
  const [accent, setAccent] = useState(department.accent)
  const [sortOrder, setSortOrder] = useState(String(department.sort_order))
  const [isActive, setIsActive] = useState(department.is_active)

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        onSave({ name, accent, sort_order: Number(sortOrder), is_active: isActive })
      }}
    >
      <div className="w-56">
        <TextField
          label="Name"
          value={name}
          required
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
      </div>
      <AccentField value={accent} onChange={setAccent} />
      <div className="w-24">
        <TextField
          label="Sort order"
          type="number"
          value={sortOrder}
          className="numeric"
          onChange={(event) => {
            setSortOrder(event.target.value)
          }}
        />
      </div>
      <label className="flex items-center gap-2 pb-2 text-[length:var(--text-sm)]">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(event) => {
            setIsActive(event.target.checked)
          }}
        />
        Active
      </label>
      <Button variant="primary" type="submit" disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </form>
  )
}

// --- one report row, with the move-to control ---------------------------------

function ReportRow({
  report,
  targets,
  onChanged,
}: {
  report: AdminReportRow
  targets: readonly DepartmentRow[]
  onChanged: () => void
}) {
  const move = useMutation({
    mutationFn: (department: string) => adminApi.moveReport(report.slug, department),
    onSuccess: onChanged,
  })

  return (
    <tr className="border-b border-[var(--color-rule)] last:border-0">
      <td className="px-2 py-1.5">
        <span className="font-medium">{report.title}</span>{' '}
        <span className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
          {report.slug}
        </span>
      </td>
      <td className="px-2 py-1.5">
        <span className="flex flex-wrap gap-1">
          {report.restricted && <Badge tone="warning">restricted</Badge>}
          {!report.is_active && <Badge tone="danger">inactive</Badge>}
          {!report.in_registry && <Badge tone="danger">not in registry</Badge>}
        </span>
      </td>
      <td className="px-2 py-1.5">
        <label className="flex items-center justify-end gap-2 text-[length:var(--text-xs)]">
          <span className="text-[var(--color-ink-muted)]">Move to</span>
          <select
            aria-label={`Move ${report.title} to another department`}
            value=""
            disabled={move.isPending || targets.length === 0}
            onChange={(event) => {
              const target = event.target.value
              if (target !== '') move.mutate(target)
            }}
            className="rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1"
          >
            <option value="">{move.isPending ? 'Moving…' : 'Choose…'}</option>
            {targets.map((department) => (
              <option key={department.key} value={department.key}>
                {department.name}
              </option>
            ))}
          </select>
        </label>
        {move.error !== null && (
          <div className="mt-1">
            <ErrorState error={move.error} />
          </div>
        )}
      </td>
    </tr>
  )
}

// --- one department -----------------------------------------------------------

function DepartmentCard({
  department,
  reports,
  allDepartments,
  onChanged,
}: {
  department: DepartmentRow
  reports: readonly AdminReportRow[]
  allDepartments: readonly DepartmentRow[]
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const save = useMutation({
    mutationFn: (patch: {
      name: string
      accent: string
      sort_order: number
      is_active: boolean
    }) => adminApi.patchDepartment(department.id, patch),
    onSuccess: () => {
      setEditing(false)
      onChanged()
    },
  })
  const remove = useMutation({
    mutationFn: () => adminApi.deleteDepartment(department.id),
    onSuccess: () => {
      setConfirmingDelete(false)
      onChanged()
    },
  })

  // Move targets: every *other* active department. Moving into an inactive one
  // is refused server-side (the report would be unreachable), so it is not
  // offered here either.
  const targets = allDepartments.filter(
    (candidate) => candidate.is_active && candidate.key !== department.key,
  )
  const deletable = department.report_count === 0 && department.grant_count === 0

  return (
    <Card
      title={department.name}
      actions={
        <>
          <Badge>{department.key}</Badge>
          <Badge>order {department.sort_order}</Badge>
          <Badge tone={department.is_active ? 'positive' : 'danger'}>
            {department.is_active ? 'active' : 'inactive'}
          </Badge>
          <Badge>
            {department.report_count} {department.report_count === 1 ? 'report' : 'reports'}
          </Badge>
          <Button
            onClick={() => {
              setEditing((value) => !value)
            }}
          >
            {editing ? 'Close' : 'Edit'}
          </Button>
          <Button
            variant="danger"
            disabled={!deletable}
            title={
              deletable
                ? undefined
                : 'Reassign its reports and remove its grants first, or deactivate it instead.'
            }
            onClick={() => {
              setConfirmingDelete(true)
            }}
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <Swatch accent={department.accent} />
        <span className="numeric text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          {department.accent.toUpperCase()}
        </span>
      </div>

      {editing && (
        <div className="mb-3 rounded-[var(--radius-md)] border border-[var(--color-rule)] p-3">
          <EditDepartment
            // Remount when the stored row changes, so the draft starts from what
            // the server holds rather than a stale render.
            key={`${String(department.sort_order)}-${department.accent}-${department.name}-${String(department.is_active)}`}
            department={department}
            saving={save.isPending}
            onSave={(patch) => {
              save.mutate(patch)
            }}
          />
          {save.error !== null && (
            <div className="mt-3">
              <ErrorState error={save.error} />
            </div>
          )}
        </div>
      )}

      {reports.length === 0 ? (
        <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          No reports assigned.
        </p>
      ) : (
        <Grid head={['Report', 'State', 'Department']}>
          {reports.map((report) => (
            <ReportRow
              key={report.slug}
              report={report}
              targets={targets}
              onChanged={onChanged}
            />
          ))}
        </Grid>
      )}

      {remove.error !== null && (
        <div className="mt-3">
          <ErrorState error={remove.error} />
        </div>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete ${department.name}?`}
        confirmLabel="Delete department"
        body={
          <>
            This department holds no reports and no grants, so nothing loses access. The{' '}
            <span className="numeric">{department.key}</span> key is gone for good — any old link to
            it stops resolving. This cannot be undone.
          </>
        }
        onCancel={() => {
          setConfirmingDelete(false)
        }}
        onConfirm={() => {
          remove.mutate()
        }}
      />
    </Card>
  )
}

// --- the page -----------------------------------------------------------------

export function AdminDepartments() {
  const queryClient = useQueryClient()
  const departments = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: adminApi.departments,
  })
  const reports = useQuery({ queryKey: ['admin', 'reports'], queryFn: adminApi.reports })

  function onChanged(): void {
    // The same fan-out every admin mutation uses: the registry view and the
    // effective-access panels resolve through `['admin']`, the signed-in user
    // through `['me']`, and the public navigation (accents, counts) through
    // `['departments']`.
    void queryClient.invalidateQueries({ queryKey: ['admin'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
    void queryClient.invalidateQueries({ queryKey: ['departments'] })
  }

  const rows = departments.data ?? []
  const nextSortOrder =
    rows.length === 0 ? 0 : Math.max(...rows.map((row) => row.sort_order)) + 1

  return (
    <div className="space-y-4">
      <Card
        title="New department"
        description="The key is lowercase and immutable — it appears in report URLs and grants reference it."
      >
        <CreateDepartment nextSortOrder={nextSortOrder} onCreated={onChanged} />
      </Card>

      {departments.isPending || reports.isPending ? (
        <Skeleton rows={5} />
      ) : departments.error !== null ? (
        <ErrorState
          error={departments.error}
          onRetry={() => {
            void departments.refetch()
          }}
        />
      ) : (
        rows.map((department) => (
          <DepartmentCard
            key={department.id}
            department={department}
            reports={(reports.data ?? []).filter((report) => report.department === department.key)}
            allDepartments={rows}
            onChanged={onChanged}
          />
        ))
      )}
    </div>
  )
}
